import { useContext } from 'react';
import {
  Body1,
  Button,
  Menu,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Title2,
} from '@fluentui/react-components';
import { AddFilled, ArrowSortRegular } from '@fluentui/react-icons';
import { SignInContext, USER_GROUPS } from '@modules/users';
import { ItemRowActionsMenu } from '@shared/components/ItemActionsMenu';
import ShortcutsLayout from '@shared/components/nav/ShortcutsLayout';
import { MenuButton } from '@shared/components/MenuButton';
import SearchInput from '@shared/components/SearchInput';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { isProductFolder, sortProductsByRecentlyOpened } from '../helpers/items.helper';
import { useNewProductDialog } from '../hooks/useNewProductDialog.hook';
import { useRootItemActions } from '../hooks/useRootItemActions.hook';
import { AccessType } from '@modules/access/types';
import type { TRootItems } from '../types/folders.types';
import type { TItemsTableRow } from '../types/items.types';
import ItemsTable from './ItemsTable/ItemsTable';

type TProps = {
  data: TRootItems;
};

const SORT_OPTIONS = { name: 'Name', recent: 'Recently opened' } as const;
type TSort = keyof typeof SORT_OPTIONS;

const AllProductsContent = ({ data }: TProps) => {
  const { user } = useContext(SignInContext);
  const { searchParams, changeMany } = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const sort: TSort = searchParams.get('sort') === 'recent' ? 'recent' : 'name';
  const { actions, rootItemDialogs } = useRootItemActions();
  const { openNewProductDialog, newProductDialog } = useNewProductDialog();

  const products = data.children.filter(isProductFolder);
  const sortedProducts = sort === 'recent' ? sortProductsByRecentlyOpened(products, data.recentProductIds) : products;
  const rows: TItemsTableRow[] = sortedProducts
    .filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
    .map((item) => ({
      key: item._id,
      item,
      submissionDueDate: data.productSubmissionInfos.find((info) => info.product === item._id)?.dueDate,
      openApprovals: data.cardStatuses[item._id]?.openApprovals,
      actionButton:
        item.accessRight >= AccessType.EDIT ? <ItemRowActionsMenu item={item} actions={actions} /> : undefined,
    }));

  return (
    <ShortcutsLayout stableScrollbarGutter>
      <div className="flex flex-col mx-auto pt-8 pb-16 px-8 max-w-screen-xl">
        {rootItemDialogs}
        {newProductDialog}

        <div className="flex items-baseline gap-3 mb-6">
          <Title2>All {TERMINOLOGY.product.lowerPlural}</Title2>
          <Body1 className="text-neutralFg-3">
            {products.length} {products.length === 1 ? TERMINOLOGY.product.lower : TERMINOLOGY.product.lowerPlural}
          </Body1>
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-4">
          {user.groups.includes(USER_GROUPS.ADMIN) && (
            <Button appearance="primary" size="medium" icon={<AddFilled />} onClick={openNewProductDialog}>
              New
            </Button>
          )}
          <Menu checkedValues={{ sort: [sort] }}>
            <MenuTrigger disableButtonEnhancement>
              <MenuButton
                appearance="subtle"
                icon={<ArrowSortRegular />}
                className="ml-auto"
                aria-label={`Sort by ${SORT_OPTIONS[sort]}`}
              >
                {SORT_OPTIONS[sort]}
              </MenuButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {(Object.keys(SORT_OPTIONS) as TSort[]).map((option) => (
                  <MenuItemRadio
                    key={option}
                    name="sort"
                    value={option}
                    onClick={() => changeMany({ sort: option === 'name' ? undefined : option })}
                  >
                    {SORT_OPTIONS[option]}
                  </MenuItemRadio>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>
          <SearchInput
            placeholder="Filter by name"
            value={search}
            onChange={(value) => changeMany({ search: value || undefined })}
            className="w-72"
          />
        </div>

        {rows.length > 0 ? (
          <ItemsTable
            rows={rows}
            dateType="modified"
            relativeDate
            show={{
              submissionDue: true,
              openApprovals: true,
              date: true,
              authors: false,
              status: false,
              approvedVersion: false,
            }}
            labels={{ date: 'Last edited' }}
            ariaLabel={`All ${TERMINOLOGY.product.lowerPlural}`}
          />
        ) : (
          <p className="text-neutral-500 py-8">No {TERMINOLOGY.product.lowerPlural} found.</p>
        )}
      </div>
    </ShortcutsLayout>
  );
};

export default AllProductsContent;
